import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { authClient } from "@/lib/auth/auth-client";
import { useORPC } from "@/providers/orpc-provider";
import { OrderTypeEnum } from "./schema";
import { validateOldOrderNumber } from "./utils";
interface UseOrderDetailParams {
  orderNumber: string;
  email?: string | null;
}
export function useOnlineOrderDetail(t0) {
  const $ = _c(13);
  const {
    orderNumber,
    email
  } = t0;
  const orpc = useORPC();
  const {
    data: session,
    isPending: isSessionPending
  } = authClient.useSession();
  const isOldOrder = validateOldOrderNumber(orderNumber);
  const userId = session?.user?.id;
  let t1;
  bb0: {
    if (isOldOrder) {
      let t2;
      if ($[0] !== email || $[1] !== orderNumber || $[2] !== orpc.ec.order.getOldOrderDetail || $[3] !== userId) {
        t2 = orpc.ec.order.getOldOrderDetail.queryOptions({
          input: {
            orderNumber,
            email: email || undefined,
            userId
          }
        });
        $[0] = email;
        $[1] = orderNumber;
        $[2] = orpc.ec.order.getOldOrderDetail;
        $[3] = userId;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[5] !== isSessionPending || $[6] !== orderNumber || $[7] !== orpc.ec.order.getOrderDetail || $[8] !== userId) {
      t2 = orpc.ec.order.getOrderDetail.queryOptions({
        input: {
          orderNumber,
          userId
        },
        enabled: !!orderNumber && !isSessionPending
      });
      $[5] = isSessionPending;
      $[6] = orderNumber;
      $[7] = orpc.ec.order.getOrderDetail;
      $[8] = userId;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = t2;
  }
  const queryOptions = t1;
  const t2 = !!orderNumber && !isSessionPending;
  let t3;
  if ($[10] !== queryOptions || $[11] !== t2) {
    t3 = {
      ...queryOptions,
      enabled: t2,
      staleTime: 0
    };
    $[10] = queryOptions;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useQuery(t3 as any);
}
interface UseOrdersParams {
  page: number;
  pageSize: number;
  [key: string]: unknown;
}
export function useOnlineOrders(t0) {
  const $ = _c(33);
  const {
    page,
    pageSize
  } = t0;
  const orpc = useORPC();
  const {
    data: session
  } = authClient.useSession();
  const userId = session?.user?.id;
  let t1;
  if ($[0] !== orpc.ec.order.getUserOrders || $[1] !== userId) {
    t1 = orpc.ec.order.getUserOrders.queryOptions({
      input: {
        page: 1,
        pageSize: 1,
        orderType: [OrderTypeEnum.SHOP] as any,
        userId
      }
    });
    $[0] = orpc.ec.order.getUserOrders;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      ...t1
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: newOrdersMeta,
    isLoading: isLoadingNewCount,
    error: newOrdersError
  } = useQuery(t2);
  let t3;
  if ($[5] !== orpc.ec.order.getOldOrders || $[6] !== userId) {
    t3 = orpc.ec.order.getOldOrders.queryOptions({
      input: {
        page: 1,
        pageSize: 1,
        userId
      }
    });
    $[5] = orpc.ec.order.getOldOrders;
    $[6] = userId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = {
      ...t3
    };
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: oldOrdersMeta,
    isLoading: isLoadingOldCount,
    error: oldOrdersError
  } = useQuery(t4);
  const totalNew = newOrdersMeta?.total ?? 0;
  const totalOld = oldOrdersMeta?.total ?? 0;
  const metaQueriesCompleted = (!isLoadingNewCount || !!newOrdersError) && (!isLoadingOldCount || !!oldOrdersError);
  let t5;
  if ($[10] !== page || $[11] !== pageSize || $[12] !== totalNew || $[13] !== totalOld || $[14] !== userId) {
    t5 = ["orders", "combined", {
      page,
      pageSize,
      totalNew,
      totalOld,
      userId
    }];
    $[10] = page;
    $[11] = pageSize;
    $[12] = totalNew;
    $[13] = totalOld;
    $[14] = userId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const queryKey = t5;
  let t6;
  if ($[16] !== newOrdersError || $[17] !== oldOrdersError || $[18] !== orpc.ec.order.getOldOrders || $[19] !== orpc.ec.order.getUserOrders || $[20] !== page || $[21] !== pageSize || $[22] !== totalNew || $[23] !== totalOld || $[24] !== userId) {
    t6 = async () => {
      if (newOrdersError && oldOrdersError) {
        try {
          const {
            items,
            total
          } = await orpc.ec.order.getUserOrders.call({
            page,
            pageSize,
            orderType: [OrderTypeEnum.SHOP] as any,
            userId
          });
          return {
            items,
            total
          };
        } catch {
          return {
            items: [],
            total: 0
          };
        }
      }
      if (oldOrdersError && !newOrdersError) {
        const {
          items: items_0,
          total: total_0
        } = await orpc.ec.order.getUserOrders.call({
          page,
          pageSize,
          orderType: [OrderTypeEnum.SHOP] as any,
          userId
        });
        return {
          items: items_0,
          total: total_0
        };
      }
      if (newOrdersError && !oldOrdersError) {
        const {
          items: items_1,
          total: total_1
        } = await orpc.ec.order.getOldOrders.call({
          page,
          pageSize,
          userId
        });
        return {
          items: items_1,
          total: total_1
        };
      }
      const total_2 = totalNew + totalOld;
      if (total_2 === 0) {
        return {
          items: [],
          total: 0
        };
      }
      const offset = (page - 1) * pageSize;
      if (offset + pageSize <= totalNew) {
        const {
          items: items_2
        } = await orpc.ec.order.getUserOrders.call({
          page,
          pageSize,
          orderType: [OrderTypeEnum.SHOP] as any,
          userId
        });
        return {
          items: items_2,
          total: total_2
        };
      }
      if (offset >= totalNew) {
        const oldOffset = offset - totalNew;
        const oldPage = Math.floor(oldOffset / pageSize) + 1;
        const itemsToSkip = oldOffset % pageSize;
        const {
          items: page1Items
        } = await orpc.ec.order.getOldOrders.call({
          page: oldPage,
          pageSize,
          userId
        });
        if (itemsToSkip === 0) {
          return {
            items: page1Items,
            total: total_2
          };
        }
        let combinedItems = page1Items.slice(itemsToSkip);
        if (combinedItems.length < pageSize && page1Items.length === pageSize) {
          const {
            items: page2Items
          } = await orpc.ec.order.getOldOrders.call({
            page: oldPage + 1,
            pageSize,
            userId
          });
          combinedItems = combinedItems.concat(page2Items);
        }
        return {
          items: combinedItems.slice(0, pageSize),
          total: total_2
        };
      }
      const newItemsToFetchCount = totalNew - offset;
      const newPageToFetch = Math.ceil(totalNew / pageSize);
      const [newOrdersResult, oldOrdersResult] = await Promise.all([orpc.ec.order.getUserOrders.call({
        page: newPageToFetch,
        pageSize,
        orderType: [OrderTypeEnum.SHOP] as any,
        userId
      }), orpc.ec.order.getOldOrders.call({
        page: 1,
        pageSize,
        userId
      })]);
      const newItems = newItemsToFetchCount > 0 ? newOrdersResult.items.slice(-newItemsToFetchCount) : [];
      const oldItemsToFetchCount = pageSize - newItems.length;
      const oldItems = oldItemsToFetchCount > 0 ? oldOrdersResult.items.slice(0, oldItemsToFetchCount) : [];
      return {
        items: [...newItems, ...oldItems],
        total: total_2
      };
    };
    $[16] = newOrdersError;
    $[17] = oldOrdersError;
    $[18] = orpc.ec.order.getOldOrders;
    $[19] = orpc.ec.order.getUserOrders;
    $[20] = page;
    $[21] = pageSize;
    $[22] = totalNew;
    $[23] = totalOld;
    $[24] = userId;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== metaQueriesCompleted || $[27] !== queryKey || $[28] !== t6) {
    t7 = {
      queryKey,
      queryFn: t6,
      enabled: metaQueriesCompleted
    };
    $[26] = metaQueriesCompleted;
    $[27] = queryKey;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const query = useQuery(t7);
  const t8 = query.isLoading || isLoadingNewCount || isLoadingOldCount;
  let t9;
  if ($[30] !== query || $[31] !== t8) {
    t9 = {
      ...query,
      isLoading: t8
    };
    $[30] = query;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}